import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, OnInit, Output,
  PLATFORM_ID, ViewEncapsulation
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { Subscription, Subject } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import {
  BrandsEnum, DeviceService, IconSizeEnum, PositionEnum, SdBannerComponent, SdHeadingComponent, SdIconComponent,
  SdLibConfig, SdTitleComponent, SizesEnum, ThemesTextEnum
} from '@seb/web-design-system/core';

import type { ISeo, ISeoParagraph } from './seo.interface';

@Component({
  selector: 'sd-seo',
  templateUrl: './seo.component.html',
  styleUrls: ['./seo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdTitleComponent,
    SdBannerComponent,
    SdHeadingComponent,
    SdIconComponent,
    TranslateModule
  ]
})
export class SdSeoComponent implements OnDestroy, OnInit, OnChanges {
  @Input()
  brand: BrandsEnum;

  @Input()
  data: ISeo;

  @Output()
  openHeadingReadMore = new Subject<boolean>();

  hasMultipleColumns: boolean;
  textConfig: PositionEnum.center | PositionEnum.left | PositionEnum.right = PositionEnum.left;
  themesEnum = ThemesTextEnum;
  sizeEnum = SizesEnum;
  iconSize = IconSizeEnum;
  isMobile: boolean;
  mobileSubscription: Subscription;
  isOpen = false;
  limit = 500;
  numberOfParagraphToDisplay: number;
  limitLastParagraph: number;
  lessThanLimit = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private cdRef: ChangeDetectorRef,
    private sdLibConfig: SdLibConfig,
    private deviceService: DeviceService,
  ) {}

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.data.paragraphs.map((paragraph: ISeoParagraph) => {
        const textArea = document.createElement('textarea');
        textArea.innerHTML = paragraph?.heading?.description;
        if (paragraph.heading?.description) {
          paragraph.heading.description = textArea.value;
        }
        return paragraph;
      });
    }
    if (this.data && this.data?.hasReadMore === undefined) {
      this.data.hasReadMore = true;
    }
    this.mobileSubscription = this.deviceService.isMobile.subscribe((isMobile) => {
      this.isMobile = isMobile;
      if (this.isMobile) {
        if (this.data?.hasReadMore) {
          this.isOpen = false;
          this.numberOfParagraphToDisplay =
            this.calculateNumberOfParagraphToDisplay();
          this.limitLastParagraph = this.calculateLimitLastParagraph();
        }
        else {
          this.isOpen = true;
          this.numberOfParagraphToDisplay = this.data.paragraphs.length;
          this.lessThanLimit = true;
        }
      }
      this.cdRef.markForCheck();
    });
  }

  ngOnChanges() {
    this.hasMultipleColumns = this.sdLibConfig.get(this.brand).seoMultipleColumnLayout;
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
  }

  calculateParagraphLength(paragraph: ISeoParagraph): number {
    let totalCharacters = 0;
    totalCharacters += paragraph.heading?.title?.length;
    totalCharacters += paragraph.heading?.description?.length;
    return totalCharacters;
  }

  calculateNumberOfParagraphToDisplay(): number {
    let countParagraph = 0;
    let countCharacters = 0;
    for (const paragraph of this.data.paragraphs) {
      countCharacters += this.calculateParagraphLength(paragraph);
      if (countCharacters >= this.limit) {
        this.lessThanLimit = false;
        break;
      } else {
        this.lessThanLimit = true;
        countParagraph += 1;
      }
    }
    return countParagraph;
  }

  calculateLimitLastParagraph(): number {
    let limitLastParagraph = this.limit;
    for (let i = 0; i < this.calculateNumberOfParagraphToDisplay(); i++) {
      limitLastParagraph -= this.calculateParagraphLength(
        this.data?.paragraphs[i],
      );
    }
    return limitLastParagraph;
  }

  openReadMore() {
    this.isOpen = !this.isOpen;
    this.openHeadingReadMore.next(this.isOpen);
  }
}
